/**
 * Data behind the admin pickup roster: every currently-enrolled after-school student,
 * with this week's attendance (Monday–Friday), grouped into pickup runs.
 *
 * Deliberately several small queries joined in memory rather than one nested select.
 * The joins are all small (about a hundred students), and keeping them separate means
 * each row shape is explicit and typed instead of relying on PostgREST embedding.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { getTodayDateStringInBusinessTZ } from '../utils/date';
import { getSessionWeekdays, shiftDateString } from '../utils/schedule';
import { currentWeekDays, mondayOfWeek, type WeekDay } from '../utils/sessionDays';
import { compareNamesByFirstName, groupIntoRuns, type RosterRun } from '../utils/rosterGrouping';
import { selectRosterEnrollments } from '../utils/rosterEnrollmentWindow';
import {
  bestOf,
  deriveSlotStatus,
  monthStart,
  type AttendanceSlotStatus,
} from '../utils/attendanceSlot';
import { REAL_SESSION_KEY, type AttendanceStatus } from '../types/attendance';

export type { AttendanceSlotStatus, WeekDay };

export interface AttendanceSlot {
  /** YYYY-MM-DD, business TZ. */
  date: string;
  status: AttendanceSlotStatus;
}

export interface RosterGuardian {
  name: string;
  phone: string | null;
  isPrimary: boolean;
}

export interface PickupRosterStudent {
  studentId: string;
  /**
   * "Last, First". Not the sort order — see `compareNamesByFirstName` — but still what the
   * CSV export names a child by, and what lets a search on a surname match.
   */
  sortName: string;
  /** "First Last" — how a name is displayed. */
  displayName: string;
  school: string | null;
  grade: string | null;
  roomNumber: string | null;
  /**
   * Object path of the student's headshot, or null when none is on file. Only the record
   * modal renders it — the run grid stays text, so a dense board is not turned into a
   * contact sheet.
   */
  photoPath: string | null;
  /** Every after-school program this student is currently enrolled in. */
  programNames: string[];
  /** True when any of those programs bills per selected day. */
  isFlex: boolean;
  hasAllergies: boolean;
  allergiesDetails: string | null;
  guardians: RosterGuardian[];
  /** Monday–Friday of the current week, always five, oldest first. */
  slots: AttendanceSlot[];
  /** The slot for today, when today is a weekday of this week. */
  today: AttendanceSlot | null;
}

/**
 * Whether a student holds an enrollment on `date` — the per-day test a future week needs.
 *
 * The current week keeps the board's long-standing rule (`selectRosterEnrollments`: who has
 * started by today). A week ahead cannot use it: a child starting that Wednesday is on the
 * roster from Wednesday, and one whose term ends that Tuesday is off it after Tuesday.
 */
interface EnrollmentWindow {
  start: string;
  end: string | null;
}

function enrolledOn(windows: readonly EnrollmentWindow[] | undefined, date: string): boolean {
  return (windows ?? []).some(w => w.start <= date && (w.end === null || w.end >= date));
}

export interface PickupRosterData {
  runs: RosterRun<PickupRosterStudent>[];
  /** Monday–Friday of the current week, and whether the program met on each. */
  week: WeekDay[];
  studentCount: number;
  schoolCount: number;
  /** Earliest start date across the active after-school programs. */
  programStartDate: string | null;
  /**
   * True when no enrollment has started yet, so the board is showing the term that is
   * about to begin rather than the one being dismissed today.
   */
  isUpcoming: boolean;
  /** First day of the term on screen — the soonest enrollment start, when upcoming. */
  termStartDate: string | null;
  today: string;
}

// ── Row shapes (snake_case, matching the tables) ──────────────────────────────

interface ProgramRow {
  id: string;
  name: string;
  billing_type: string;
  schedule_cron: string | null;
  start_date: string | null;
}

interface EnrollmentRow {
  id: string;
  student_id: string;
  program_id: string;
  enrollment_start_date: string;
  enrollment_end_date: string | null;
}

interface StudentMetadata {
  school?: string;
  grade?: string;
  room_number?: string;
  bhcs_waiver?: { hasAllergies?: boolean; allergiesDetails?: string };
}

interface StudentRow {
  id: string;
  first_name: string;
  last_name: string;
  active: boolean;
  metadata: StudentMetadata | null;
  photo_path: string | null;
}

interface StudentParentRow {
  student_id: string;
  is_primary: boolean | null;
  parent: { first_name: string | null; last_name: string | null; phone: string | null } | null;
}

interface AttendanceRow {
  student_id: string;
  session_date: string;
  status: AttendanceStatus;
}

interface FlexPlanRow {
  enrollment_id: string;
  plan_month: string;
  dates: string[] | null;
}

export const pickupRosterKeys = {
  all: (today: string, weekOffset = 0) => ['admin-pickup-roster', today, weekOffset] as const,
};

/**
 * @param weekOffset Weeks ahead of the current one; 0 is this week. A future week shows who
 *   is expected — Flex picks, enrollments starting or ending mid-week — with no attendance
 *   yet except absences reported in advance.
 */
export function useAdminPickupRoster(weekOffset = 0) {
  const today = getTodayDateStringInBusinessTZ();
  const isFutureWeek = weekOffset > 0;
  // Any day inside the week on screen. `currentWeekDays` only reads its week.
  const weekAnchor = isFutureWeek ? shiftDateString(mondayOfWeek(today), 7 * weekOffset) : today;

  return useQuery<PickupRosterData>({
    queryKey: pickupRosterKeys.all(today, weekOffset),
    queryFn: async () => {
      // 1. The active after-school programs, which define both the population and the
      //    session calendar.
      const { data: programData, error: programError } = await supabase
        .from('programs')
        .select('id, name, billing_type, schedule_cron, start_date')
        .eq('type', 'after-school')
        .eq('active', true);
      if (programError) throw programError;

      const programs = (programData ?? []) as ProgramRow[];
      const empty: PickupRosterData = {
        runs: [],
        week: currentWeekDays({ today: weekAnchor, weekdays: new Set() }),
        studentCount: 0,
        schoolCount: 0,
        programStartDate: null,
        isUpcoming: false,
        termStartDate: null,
        today,
      };
      if (programs.length === 0) return empty;

      const programById = new Map(programs.map(p => [p.id, p]));
      const programStartDate = programs
        .map(p => p.start_date)
        .filter((d): d is string => Boolean(d))
        .sort()[0] ?? null;

      // The union of every program's meeting days — a session day is any day on which
      // at least one after-school program ran.
      const weekdays = new Set<number>();
      for (const program of programs) {
        if (!program.schedule_cron) continue;
        for (const day of getSessionWeekdays(program.schedule_cron)) weekdays.add(day);
      }
      const week = currentWeekDays({
        today: weekAnchor,
        weekdays: weekdays.size > 0 ? weekdays : undefined,
        notBefore: programStartDate,
      });
      const sessionDates = week.filter(day => day.isSession).map(day => day.date);

      // 2. Enrollments whose term has not ended. Deliberately NOT filtered to "started
      //    already": on the day before the school year begins, every enrollment is still
      //    in the future, and an empty board is the least useful thing to show someone
      //    preparing the first day's dismissal.
      //    A future week asks the same of its own Monday instead, and then keeps whoever
      //    overlaps it at all; which days each child is on is settled per slot below.
      const weekStart = week[0].date;
      const weekEnd = week[week.length - 1].date;
      const { data: enrollmentData, error: enrollmentError } = await supabase
        .from('enrollments')
        .select('id, student_id, program_id, enrollment_start_date, enrollment_end_date')
        .in('program_id', programs.map(p => p.id))
        .eq('is_active', true)
        .gte('enrollment_end_date', isFutureWeek ? weekStart : today);
      if (enrollmentError) throw enrollmentError;

      const { enrollments, isUpcoming, termStartDate } = isFutureWeek
        ? {
            enrollments: ((enrollmentData ?? []) as EnrollmentRow[]).filter(
              e => e.enrollment_start_date <= weekEnd,
            ),
            isUpcoming: false,
            termStartDate: null,
          }
        : selectRosterEnrollments((enrollmentData ?? []) as EnrollmentRow[], today);

      if (enrollments.length === 0) {
        return { ...empty, week, programStartDate };
      }

      const studentIds = Array.from(new Set(enrollments.map(e => e.student_id)));

      // 3–6. Everything keyed off those students, in parallel.
      const flexEnrollmentIds = enrollments
        .filter(e => programById.get(e.program_id)?.billing_type === 'flex_monthly')
        .map(e => e.id);
      const planMonths = Array.from(new Set(sessionDates.map(monthStart)));

      const [studentsRes, guardiansRes, attendanceRes, flexRes] = await Promise.all([
        supabase
          .from('students')
          .select('id, first_name, last_name, active, metadata, photo_path')
          .in('id', studentIds),
        supabase
          .from('student_parents')
          .select('student_id, is_primary, parent:parents(first_name, last_name, phone)')
          .in('student_id', studentIds),
        sessionDates.length > 0
          ? supabase
              .from('attendance_records')
              .select('student_id, session_date, status')
              .in('student_id', studentIds)
              .in('session_date', sessionDates)
              // The same after-school programs the population came from. Rows are merged
              // per student-day by `bestOf`, and a child's enrichment class writes its own
              // row against its own program: an art or math teacher releasing their class
              // at the end of the hour would otherwise outrank the after-school check-in
              // and tell this board a child on site had already gone home.
              .in('program_id', programs.map(p => p.id))
              // Real after-school sessions only. A virtual Chinese or Math row carries the
              // after-school program_id, so the filter above does not exclude it, and its
              // register would land in the same bucket as the handover.
              .eq('virtual_class_key', REAL_SESSION_KEY)
          : Promise.resolve({ data: [], error: null }),
        flexEnrollmentIds.length > 0 && planMonths.length > 0
          ? supabase
              .from('flex_month_plans')
              .select('enrollment_id, plan_month, dates')
              .in('enrollment_id', flexEnrollmentIds)
              .in('plan_month', planMonths)
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (studentsRes.error) throw studentsRes.error;
      if (guardiansRes.error) throw guardiansRes.error;
      if (attendanceRes.error) throw attendanceRes.error;
      if (flexRes.error) throw flexRes.error;

      const students = (studentsRes.data ?? []) as StudentRow[];
      const guardianRows = (guardiansRes.data ?? []) as unknown as StudentParentRow[];
      const attendanceRows = (attendanceRes.data ?? []) as AttendanceRow[];
      const flexRows = (flexRes.data ?? []) as FlexPlanRow[];

      // Best record per (student, day).
      const bestStatus = new Map<string, AttendanceStatus>();
      for (const row of attendanceRows) {
        const key = `${row.student_id}|${row.session_date}`;
        const current = bestStatus.get(key);
        bestStatus.set(key, current === undefined ? row.status : bestOf(current, row.status));
      }

      // Days a flex student chose, per student. A month with no plan row contributes
      // nothing: "no plan on file" is not the same as "chose no days", so those days fall
      // through to whatever attendance says.
      const flexDaysByStudent = new Map<string, Set<string>>();
      const flexMonthsByStudent = new Map<string, Set<string>>();
      const studentByEnrollment = new Map(enrollments.map(e => [e.id, e.student_id]));
      for (const row of flexRows) {
        const studentId = studentByEnrollment.get(row.enrollment_id);
        if (!studentId) continue;
        const days = flexDaysByStudent.get(studentId) ?? new Set<string>();
        for (const date of row.dates ?? []) days.add(date);
        flexDaysByStudent.set(studentId, days);
        const months = flexMonthsByStudent.get(studentId) ?? new Set<string>();
        months.add(row.plan_month.slice(0, 10));
        flexMonthsByStudent.set(studentId, months);
      }

      const guardiansByStudent = new Map<string, RosterGuardian[]>();
      for (const row of guardianRows) {
        if (!row.parent) continue;
        const name = [row.parent.first_name, row.parent.last_name].filter(Boolean).join(' ').trim();
        if (!name) continue;
        const list = guardiansByStudent.get(row.student_id) ?? [];
        list.push({ name, phone: row.parent.phone, isPrimary: row.is_primary === true });
        guardiansByStudent.set(row.student_id, list);
      }
      for (const list of guardiansByStudent.values()) {
        list.sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || a.name.localeCompare(b.name));
      }

      const windowsByStudent = new Map<string, EnrollmentWindow[]>();
      for (const enrollment of enrollments) {
        const list = windowsByStudent.get(enrollment.student_id) ?? [];
        list.push({ start: enrollment.enrollment_start_date, end: enrollment.enrollment_end_date });
        windowsByStudent.set(enrollment.student_id, list);
      }

      const programsByStudent = new Map<string, string[]>();
      const flexStudentIds = new Set<string>();
      for (const enrollment of enrollments) {
        const program = programById.get(enrollment.program_id);
        if (!program) continue;
        const list = programsByStudent.get(enrollment.student_id) ?? [];
        if (!list.includes(program.name)) list.push(program.name);
        programsByStudent.set(enrollment.student_id, list);
        if (program.billing_type === 'flex_monthly') flexStudentIds.add(enrollment.student_id);
      }

      const entries: PickupRosterStudent[] = students
        // A deactivated student with a stale active enrollment must not appear on a
        // pickup list — nobody is collecting them.
        .filter(student => student.active)
        .map(student => {
          const metadata = student.metadata ?? {};
          const waiver = metadata.bhcs_waiver ?? {};
          const isFlex = flexStudentIds.has(student.id);
          const flexDays = flexDaysByStudent.get(student.id);
          const flexMonths = flexMonthsByStudent.get(student.id);

          const windows = windowsByStudent.get(student.id);
          const slots: AttendanceSlot[] = week.map(({ date, isSession }) => ({
            date,
            status: isFutureWeek && isSession && !enrolledOn(windows, date) ? 'not_enrolled' : deriveSlotStatus({
              date,
              isFlex,
              flexMonths,
              flexDays,
              recordStatus: bestStatus.get(`${student.id}|${date}`),
              isSessionDay: isSession,
              today,
            }),
          }));

          const first = student.first_name?.trim() ?? '';
          const last = student.last_name?.trim() ?? '';
          return {
            studentId: student.id,
            sortName: last ? `${last}, ${first}` : first,
            displayName: `${first} ${last}`.trim(),
            school: metadata.school ?? null,
            grade: metadata.grade ?? null,
            roomNumber: metadata.room_number ?? null,
            photoPath: student.photo_path ?? null,
            programNames: programsByStudent.get(student.id) ?? [],
            isFlex,
            hasAllergies: waiver.hasAllergies === true,
            allergiesDetails: waiver.allergiesDetails ?? null,
            guardians: guardiansByStudent.get(student.id) ?? [],
            slots,
            today: slots.find(slot => slot.date === today) ?? null,
          };
        })
        // Sorted before grouping: `groupBySchoolThenGrade` preserves input order inside a
        // grade, so this one call orders every grade on the screen. By first name, and by
        // the same rule as the teacher roster — the two screens work the same dismissal
        // line, and an admin standing beside a teacher should not be reading a different
        // order to them.
        .sort((a, b) => compareNamesByFirstName(a.displayName, b.displayName));

      const runs = groupIntoRuns(entries);
      return {
        runs,
        week,
        studentCount: entries.length,
        schoolCount: new Set(runs.map(r => r.school)).size,
        programStartDate,
        isUpcoming,
        termStartDate,
        today,
      };
    },
    staleTime: 60_000,
    // Stepping between weeks keeps the last board on screen until the next one arrives,
    // rather than blanking the page (and its week controls) for every click.
    placeholderData: previous => previous,
  });
}
